import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import QuoteAccordion from "../components/quoteTable";
import { useParams, Link, useNavigate } from "react-router-dom";
import AcceptRejectButton from "../components/acceptRejectButton";
import TeamMemberCard, { getFirstName } from "../components/teamMemberCard";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import imperialHeroMobile from "../assets/dhol-players-uk-wedding-hero-mobile.webp";
import imperialHeroDesktop from "../assets/dhol-players-uk-wedding-hero.webp";
import { Fade, Grow, Collapse } from "@mui/material";
import { Helmet } from "react-helmet";
import { getCsrfToken } from "../utils/csrf";
import {
  Container,
  Paper,
  Typography,
  Box,
  LinearProgress,
  CardMedia,
  Alert,
  useTheme,
  useMediaQuery,
  Button,
  Chip,
  Divider,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  ThumbUp as ThumbUpIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as BankIcon,
  ContentCopy as CopyIcon,
  Celebration as CelebrationIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";

const queryClient = new QueryClient();

// Stage definitions
const STAGES = {
  QUOTE: "quote",
  CONFIRM: "confirm",
  PAYMENT: "payment",
  SUCCESS: "success",
};

const getStageOrder = (hasDeposit) => [
  STAGES.QUOTE,
  STAGES.CONFIRM,
  hasDeposit ? STAGES.PAYMENT : STAGES.SUCCESS,
];

const getStageConfig = (hasDeposit) => ({
  [STAGES.QUOTE]: {
    label: "Review Quote",
    icon: ThumbUpIcon,
    description: "Review and accept your quote",
  },
  [STAGES.CONFIRM]: {
    label: "Confirm Details",
    icon: DescriptionIcon,
    description: "Confirm your booking information",
  },
  [STAGES.PAYMENT]: {
    label: "Pay Deposit",
    icon: PaymentIcon,
    description: "Secure your booking",
  },
  [STAGES.SUCCESS]: {
    label: "All Done!",
    icon: CelebrationIcon,
    description: "You're all set",
  },
});

// Vertical Timeline Component
const VerticalTimeline = memo(({ currentStage, completedStages, onStageClick, mainColor, accentColor, hasDeposit, daysUntilExpiry, isAccepted, teamMember }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const stageOrder = getStageOrder(hasDeposit);
  const stageConfig = getStageConfig(hasDeposit);

  const getStageStatus = (stage) => {
    if (completedStages.includes(stage)) return "completed";
    if (stage === currentStage) return "active";
    return "pending";
  };

  if (isMobile) {
    // Compact horizontal stepper for mobile
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
        {stageOrder.map((stage, index) => {
          const status = getStageStatus(stage);
          const config = stageConfig[stage];
          const IconComponent = config.icon;
          const isClickable = completedStages.includes(stage) || stage === currentStage;

          return (
            <Box
              key={stage}
              sx={{
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              {/* Connector line before (not for first) */}
              {index > 0 && (
                <Box
                  sx={{
                    width: { xs: 24, sm: 40 },
                    height: 3,
                    backgroundColor: completedStages.includes(stageOrder[index - 1])
                      ? mainColor
                      : "#e0e0e0",
                    mt: "18px", // Half of circle height (40/2 - line height/2)
                    flexShrink: 0,
                  }}
                />
              )}

              {/* Step column: circle + label */}
              <Box
                onClick={() => isClickable && onStageClick(stage)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: isClickable ? "pointer" : "default",
                  opacity: status === "pending" ? 0.5 : 1,
                  width: { xs: 70, sm: 80 },
                }}
              >
                {/* Circle */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      status === "completed" || status === "active"
                        ? mainColor
                        : "#e0e0e0",
                    color: "white",
                    boxShadow:
                      status === "active"
                        ? `0 4px 12px rgba(56,0,69,0.4)`
                        : "none",
                    transition: "background-color 0.2s, box-shadow 0.2s",
                    flexShrink: 0,
                  }}
                >
                  {status === "completed" ? (
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <IconComponent sx={{ fontSize: 20 }} />
                  )}
                </Box>

                {/* Label */}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: status === "active" ? 600 : 400,
                    color: status === "pending" ? "#999" : mainColor,
                    textAlign: "center",
                    fontSize: "0.65rem",
                    mt: 1,
                    lineHeight: 1.2,
                  }}
                >
                  {config.label}
                </Typography>
              </Box>
            </Box>
          );
        })}
        </Box>
        {!isAccepted && daysUntilExpiry != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              backgroundColor: "rgba(56,0,69,0.05)",
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 13, color: mainColor, opacity: 0.7 }} />
            <Typography variant="caption" sx={{ color: mainColor, fontWeight: 600, opacity: 0.8 }}>
              Valid for {daysUntilExpiry} more {daysUntilExpiry === 1 ? "day" : "days"}
            </Typography>
          </Box>
        )}
        {teamMember?.name && (
          <>
            <Divider flexItem sx={{ borderColor: "rgba(0,0,0,0.06)" }} />
            <TeamMemberCard member={teamMember} mainColor={mainColor} accentColor={accentColor} compact />
          </>
        )}
      </Box>
    );
  }

  // Desktop vertical timeline
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 3,
        backgroundColor: "white",
        borderRadius: "24px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      }}
    >
      {stageOrder.map((stage, index) => {
        const status = getStageStatus(stage);
        const config = stageConfig[stage];
        const IconComponent = config.icon;
        const isClickable = completedStages.includes(stage) || stage === currentStage;

        return (
          <Box key={stage}>
            {/* Connector line before (not for first) */}
            {index > 0 && (
              <Box
                sx={{
                  width: 3,
                  height: 32,
                  backgroundColor: completedStages.includes(stageOrder[index - 1])
                    ? mainColor
                    : "#e0e0e0",
                  ml: "22px",
                }}
              />
            )}

            {/* Stage circle and label */}
            <Box
              onClick={() => isClickable && onStageClick(stage)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                cursor: isClickable ? "pointer" : "default",
                opacity: status === "pending" ? 0.5 : 1,
                transition: "opacity 0.2s, transform 0.2s",
                "&:hover": isClickable
                  ? { transform: "translateX(4px)" }
                  : {},
                py: 1,
              }}
            >
              {/* Circle */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    status === "completed" || status === "active"
                      ? mainColor
                      : "#e0e0e0",
                  color: "white",
                  boxShadow:
                    status === "active"
                      ? `0 4px 12px rgba(56,0,69,0.4)`
                      : "none",
                  transition: "background-color 0.2s, box-shadow 0.2s",
                  flexShrink: 0,
                }}
              >
                {status === "completed" ? (
                  <CheckCircleIcon sx={{ fontSize: 24 }} />
                ) : (
                  <IconComponent sx={{ fontSize: 24 }} />
                )}
              </Box>

              {/* Label */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: status === "active" ? 700 : 500,
                    color: status === "pending" ? "#999" : mainColor,
                  }}
                >
                  {config.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", display: "block" }}
                >
                  {config.description}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
      {!isAccepted && daysUntilExpiry != null && (
        <>
          <Divider sx={{ mt: 2, mb: 1.5, borderColor: "rgba(0,0,0,0.06)" }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backgroundColor: "rgba(56,0,69,0.05)",
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 15, color: mainColor, opacity: 0.7 }} />
            <Typography variant="caption" sx={{ color: mainColor, fontWeight: 600, opacity: 0.8 }}>
              Valid for {daysUntilExpiry} more {daysUntilExpiry === 1 ? "day" : "days"}
            </Typography>
          </Box>
        </>
      )}
      {teamMember?.name && (
        <>
          <Divider sx={{ mt: 2.5, mb: 2.5, borderColor: "rgba(0,0,0,0.06)" }} />
          <TeamMemberCard member={teamMember} mainColor={mainColor} accentColor={accentColor} />
        </>
      )}
    </Box>
  );
});

const QuotePage = () => {
  const { quoteId, stage } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Determine current stage and admin view
  const isAdminView = stage === "admin";
  const currentStage = isAdminView ? STAGES.QUOTE : stage || STAGES.QUOTE;

  // Refs for scrolling
  const quoteSectionRef = useRef(null);
  const confirmSectionRef = useRef(null);
  const finalSectionRef = useRef(null);

  // Quote state
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Confirmation form state — persisted to sessionStorage so navigation away doesn't clear it
  const formStorageKey = `quoteForm_${quoteId}`;
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(formStorageKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      firstname: "",
      lastname: "",
      email: "",
      phone: "",
      address: "",
      signatureName: "",
      termsAccepted: false,
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Persist form data to sessionStorage on every change
  useEffect(() => {
    try { sessionStorage.setItem(formStorageKey, JSON.stringify(formData)); } catch {}
  }, [formData, formStorageKey]);

  // Confirmation data (for payment stage)
  const [confirmation, setConfirmation] = useState(null);

  // Section expansion state
  const [quoteSectionExpanded, setQuoteSectionExpanded] = useState(true);
  const [confirmSectionExpanded, setConfirmSectionExpanded] = useState(true);

  // Local option selections (not saved until quote is accepted)
  const [localSelections, setLocalSelections] = useState({});

  // Handle local option selection change
  const handleSelectionChange = (eventId, optionId) => {
    setLocalSelections(prev => ({ ...prev, [eventId]: optionId }));
  };

  // Check if all events with options have a selection (local or server)
  const checkAllOptionsSelected = () => {
    if (!quote?.eventsList) return true;
    return quote.eventsList.every(event => {
      if (!event.hasOptions) return true;
      // Check local selection first, then server state
      return localSelections[event.id] || event.options?.some(opt => opt.isSelected);
    });
  };

  // Memoize events with options array to prevent unnecessary re-renders
  const eventsWithOptions = useMemo(() =>
    quote?.eventsList?.filter(e => e.hasOptions) || [],
    [quote?.eventsList]
  );

  // Styling values
  const paperRadius = "24px";
  const mainColor = "#380045";
  const accentColor = "#D4AF37";

  // Format a numeric amount as GBP (UK style, thousands separated), guarding
  // against null/undefined
  const formatCurrency = (value) =>
    value == null
      ? null
      : `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Shared surface so the confirmation-page cards (countdown, balance, bank
  // details) are uniform in width, styling, and how they scale down. Width is
  // left auto (not 100%) so the padding stays inside the box under the app's
  // content-box sizing — otherwise the cards overflow their parent on mobile.
  const infoCardSx = {
    maxWidth: 420,
    mx: "auto",
    p: { xs: 3, md: 4 },
    backgroundColor: "rgba(56,0,69,0.03)",
    border: "1px solid rgba(56,0,69,0.08)",
    borderRadius: 3,
  };

  // Sanitize values
  const sanitizeValue = (v) => {
    return v === null || v === undefined || v === "" ? "-" : v;
  };

  const sanitizeQuote = (q) => {
    if (!q) return { name: "-", eventsList: [], expensesList: [], totalPrice: null, isAccepted: false, allOptionsSelected: true, externalNotes: null };
    return {
      ...q,
      name: sanitizeValue(q.name),
      totalPrice: q.totalPrice ?? null,
      expensesList: Array.isArray(q.expensesList)
        ? q.expensesList.map((ex) => ({
            id: ex.id,
            category: ex.category,
            label: ex.label || ex.category || "-",
            description: ex.description,
            amount: ex.amount,
            quantity: ex.quantity,
            total: ex.total,
          }))
        : [],
      eventsList: Array.isArray(q.eventsList)
        ? q.eventsList.map((ev) => ({
            id: ev.id,
            name: sanitizeValue(ev.name),
            location: sanitizeValue(ev.location),
            startTime: ev.startTime || null,
            numberOfDrummers:
              ev.numberOfDrummers === null || ev.numberOfDrummers === undefined || ev.numberOfDrummers === ""
                ? "-"
                : ev.numberOfDrummers,
            price: ev.price === null || ev.price === undefined || ev.price === "" ? "-" : ev.price,
            hasOptions: !!ev.hasOptions,
            options: Array.isArray(ev.options)
              ? ev.options.map((opt) => ({
                  id: opt.id,
                  optionLabel: opt.optionLabel,
                  numberOfDrummers: opt.numberOfDrummers,
                  price: opt.price,
                  description: opt.description,
                  isSelected: !!opt.isSelected,
                }))
              : [],
          }))
        : [],
      isAccepted: !!q.isAccepted,
      allOptionsSelected: q.allOptionsSelected !== false,
    };
  };

  // Fetch quote
  const fetchQuote = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_BACKEND_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken() || "",
        },
        credentials: "include",
        body: JSON.stringify({
          query: `
            query GetQuote($uuid: ID!) {
              getQuote(uuid: $uuid) {
                uuid
                status
                isExpired
                daysUntilExpiry
                name
                totalPrice
                eventsList {
                  id
                  name
                  location
                  numberOfDrummers
                  price
                  startTime
                  hasOptions
                  options {
                    id
                    optionLabel
                    numberOfDrummers
                    price
                    description
                    isSelected
                  }
                }
                expensesList {
                  id
                  category
                  label
                  description
                  amount
                  quantity
                  total
                }
                isAccepted
                allOptionsSelected
                externalNotes
                teamMember {
                  name
                  jobTitle
                  photoUrl
                  phoneNumber
                  bio
                }
              }
            }
          `,
          variables: { uuid: quoteId },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const { status, isExpired } = result.data.getQuote ?? {};
      if (status === "CANCELLED" || status === "EXPIRED" || isExpired) {
        setQuote({ status: status ?? "EXPIRED", isExpired: !!isExpired });
        setLoading(false);
        return;
      }

      const sanitized = sanitizeQuote(result.data.getQuote);
      setQuote(sanitized);

      // Pre-fill form with customer name
      if (sanitized.name && sanitized.name !== "-") {
        setFormData((prev) => ({
          ...prev,
          firstname: sanitized.name,
        }));
      }

      setLoading(false);
    } catch (err) {
      setError("Something went wrong. Please try again later.");
      setLoading(false);
    }
  };

  // Fetch confirmation data (for confirm/payment stages)
  const fetchConfirmation = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_BACKEND_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken() || "",
        },
        credentials: "include",
        body: JSON.stringify({
          query: `
            query GetBookingConfirmationByQuote($quoteId: ID!) {
              getBookingConfirmationByQuote(quoteId: $quoteId) {
                confirmationId
                status
                bookingStatus
                isAlreadyConfirmed
                totalPrice
                depositAmount
                depositPaid
                remainingBalance
                paymentReference
                daysUntilEvent
                customer {
                  firstname
                  lastname
                  email
                  phone
                  address
                }
                events {
                  name
                  location
                  numberOfDrummers
                  price
                  eventDate
                  startTime
                }
              }
            }
          `,
          variables: { quoteId },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        // Confirmation not found - that's okay for quote stage
        return;
      }

      const data = result.data.getBookingConfirmationByQuote;
      setConfirmation(data);

      // Pre-fill form with customer data
      if (data?.customer) {
        setFormData((prev) => ({
          ...prev,
          firstname: data.customer.firstname || prev.firstname,
          lastname: data.customer.lastname || prev.lastname,
          email: data.customer.email || prev.email,
          phone: data.customer.phone || prev.phone,
          address: data.customer.address || prev.address,
        }));
      }
    } catch (err) {
      // Silently fail - confirmation might not exist yet
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (quoteId) {
      fetchQuote();
    } else {
      setQuote(sanitizeQuote(null));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  // Fetch confirmation when quote is accepted
  useEffect(() => {
    if (quote?.isAccepted && quoteId) {
      fetchConfirmation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.isAccepted, quoteId]);

  // Determine if deposit is required and if it's been paid
  const hasDeposit = confirmation?.depositAmount && confirmation.depositAmount > 0;
  const isDepositPaid = confirmation?.bookingStatus && confirmation.bookingStatus !== "PENDING";
  // Show success if: no deposit required OR deposit has been paid
  const finalStage = (hasDeposit && !isDepositPaid) ? STAGES.PAYMENT : STAGES.SUCCESS;

  // Outstanding balance — authoritative, computed server-side. Never derive it
  // client-side. `null` for expired / all-events-in-past bookings.
  const remainingBalance = confirmation?.remainingBalance;
  const depositPaid = confirmation?.depositPaid;
  const hasRemainingBalance = remainingBalance != null && remainingBalance > 0;

  // Static bank-transfer details (not provided by the backend). Shown wherever
  // there is a balance to pay.
  const renderBankDetails = () => (
    <Box sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mb: 3,
        }}
      >
        <BankIcon sx={{ color: mainColor }} />
        <Typography variant="h6" sx={{ color: mainColor, fontWeight: 600 }}>
          Bank Transfer Details
        </Typography>
      </Box>

      <Box sx={infoCardSx}>
        {[
          { label: "Account Name", value: "Imperial Drummers" },
          { label: "Sort Code", value: "04-00-03" },
          { label: "Account Number", value: "21785738" },
          { label: "Reference", value: confirmation?.paymentReference },
        ].map((item, index, arr) => (
          <Box
            key={item.label}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 0.25, sm: 1 },
              py: 1.5,
              borderBottom: index < arr.length - 1 ? "1px solid rgba(0,0,0,0.08)" : "none",
            }}
          >
            <Typography variant="body2" sx={{ color: "#666", flexShrink: 0 }}>
              {item.label}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                maxWidth: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  fontFamily: "monospace",
                  minWidth: 0,
                  overflowWrap: "anywhere",
                  textAlign: { xs: "left", sm: "right" },
                }}
              >
                {item.value}
              </Typography>
              <Tooltip title="Copy" arrow>
                <IconButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(item.value || "")}
                  sx={{ color: "#999", flexShrink: 0, "&:hover": { color: mainColor } }}
                >
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>

      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          mt: 3,
          color: "#666",
          fontStyle: "italic",
        }}
      >
        Please use the reference above when making your payment
      </Typography>
    </Box>
  );

  // Redirect based on quote state and stage
  useEffect(() => {
    if (!loading && quote && !isAdminView) {
      // If quote is NOT accepted and user tries to access confirm/payment/success, redirect to quote
      if (!quote.isAccepted && (currentStage === STAGES.CONFIRM || currentStage === STAGES.PAYMENT || currentStage === STAGES.SUCCESS)) {
        navigate(`/quote/${quoteId}`, { replace: true });
        return;
      }
      // If quote is accepted but confirmation not done, and user tries to access payment/success, redirect to confirm
      if (quote.isAccepted && !confirmation?.isAlreadyConfirmed && (currentStage === STAGES.PAYMENT || currentStage === STAGES.SUCCESS)) {
        navigate(`/quote/${quoteId}/${STAGES.CONFIRM}`, { replace: true });
        return;
      }
      // If quote is accepted and we're on quote stage, go to confirm
      if (quote.isAccepted && currentStage === STAGES.QUOTE) {
        navigate(`/quote/${quoteId}/${STAGES.CONFIRM}`, { replace: true });
      }
      // If confirmation is already done and we're on confirm stage, go to final stage (payment or success)
      if (confirmation?.isAlreadyConfirmed && currentStage === STAGES.CONFIRM) {
        navigate(`/quote/${quoteId}/${finalStage}`, { replace: true });
      }
    }
  }, [loading, quote, confirmation, currentStage, isAdminView, quoteId, navigate, finalStage]);

  // Update section expansion based on stage
  useEffect(() => {
    if (currentStage === STAGES.CONFIRM) {
      setQuoteSectionExpanded(false);
      setConfirmSectionExpanded(true);
    } else if (currentStage === STAGES.PAYMENT || currentStage === STAGES.SUCCESS) {
      setQuoteSectionExpanded(false);
      setConfirmSectionExpanded(false);
    } else {
      setQuoteSectionExpanded(true);
      setConfirmSectionExpanded(true);
    }
  }, [currentStage]);

  // Scroll to section when stage changes
  useEffect(() => {
    const scrollToSection = () => {
      let ref = null;
      if (currentStage === STAGES.QUOTE) ref = quoteSectionRef;
      else if (currentStage === STAGES.CONFIRM) ref = confirmSectionRef;
      else if (currentStage === STAGES.PAYMENT || currentStage === STAGES.SUCCESS) ref = finalSectionRef;

      if (ref?.current) {
        setTimeout(() => {
          ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    };

    if (!loading) {
      scrollToSection();
    }
  }, [currentStage, loading]);

  // Determine completed stages
  const getCompletedStages = () => {
    const completed = [];
    if (quote?.isAccepted) completed.push(STAGES.QUOTE);
    if (confirmation?.isAlreadyConfirmed) completed.push(STAGES.CONFIRM);
    return completed;
  };

  // Handle stage click
  const handleStageClick = (stage) => {
    navigate(`/quote/${quoteId}/${stage}`);
  };

  // Handle form change - memoized to prevent unnecessary re-renders
  const handleChange = useCallback((field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle confirm submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);

    if (!formData.termsAccepted) {
      setSubmitError("Please accept the terms and conditions to proceed.");
      return;
    }

    if (!formData.signatureName || formData.signatureName.trim().length < 2) {
      setSubmitError("Please provide your full name as a signature.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(process.env.REACT_APP_BACKEND_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken() || "",
        },
        credentials: "include",
        body: JSON.stringify({
          query: `
            mutation SubmitBookingConfirmation(
              $confirmationId: UUID!
              $customerInfo: CustomerInfoInput!
              $signatureName: String!
              $termsAccepted: Boolean!
            ) {
              submitBookingConfirmation(
                confirmationId: $confirmationId
                customerInfo: $customerInfo
                signatureName: $signatureName
                termsAccepted: $termsAccepted
              ) {
                success
                message
                responseCode
              }
            }
          `,
          variables: {
            confirmationId: confirmation.confirmationId,
            customerInfo: {
              firstname: formData.firstname,
              lastname: formData.lastname,
              email: formData.email,
              phone: formData.phone,
              address: formData.address,
            },
            signatureName: formData.signatureName,
            termsAccepted: formData.termsAccepted,
          },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const data = result.data.submitBookingConfirmation;

      if (data.success) {
        // Determine final stage based on deposit requirement and payment status
        const depositRequired = confirmation?.depositAmount && confirmation.depositAmount > 0;
        const depositAlreadyPaid = confirmation?.bookingStatus && confirmation.bookingStatus !== "PENDING";
        const navigateToStage = (depositRequired && !depositAlreadyPaid)
          ? STAGES.PAYMENT
          : STAGES.SUCCESS;

        setConfirmation((prev) => ({
          ...prev,
          isAlreadyConfirmed: true,
          status: "CONFIRMED",
        }));
        try { sessionStorage.removeItem(formStorageKey); } catch {}
        // Navigate to final stage (payment or success)
        navigate(`/quote/${quoteId}/${navigateToStage}`);
      } else {
        setSubmitError(data.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      setSubmitError(err.message || "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Server-computed total (events + expenses). Prefer the authoritative total;
  // only fall back to a client sum for legacy quotes where it's unavailable.
  const getTotalPrice = () => {
    if (confirmation?.totalPrice != null) return confirmation.totalPrice;
    if (quote?.totalPrice != null) return quote.totalPrice;
    if (Array.isArray(quote?.eventsList)) {
      return quote.eventsList.reduce((total, event) => total + (parseFloat(event.price) || 0), 0);
    }
    return 0;
  };

  const completedStages = getCompletedStages();
  const isConfirmStage = currentStage === STAGES.CONFIRM;
  const isPaymentStage = currentStage === STAGES.PAYMENT;
  const isSuccessStage = currentStage === STAGES.SUCCESS;

  return (
    <Fade in={true} timeout={800}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#faf9f6",
          pb: 12,
        }}
      >
        <Helmet>
          <title>
            {isSuccessStage
              ? "Booking Confirmed"
              : isPaymentStage
              ? "Pay Deposit"
              : isConfirmStage
              ? "Confirm Booking"
              : "Your Quote"}{" "}
            | Imperial Drummers
          </title>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>

        {/* Hero Image Section */}
        <Box sx={{
          position: "relative",
          height: { xs: "30vh", md: "40vh" },
          overflow: "hidden",
          backgroundColor: "#380045"
        }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(to bottom, rgba(56,0,69,0.2) 0%, rgba(56,0,69,0.9) 100%)",
              zIndex: 1,
            }}
          />
          <CardMedia
            component="img"
            image={isMobile ? imperialHeroMobile : imperialHeroDesktop}
            alt="Imperial Drumming"
            loading="eager"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 30%",
            }}
          />

          {/* Hero Content Overlay */}
          <Container
            maxWidth="lg"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              pb: { xs: 3, md: 5 },
            }}
          >
            <Grow in={!loading} timeout={1000}>
              <Box>
                {isAdminView && (
                  <Chip
                    icon={<AdminIcon style={{ color: "white" }} />}
                    label="Admin Preview"
                    sx={{
                      mb: 2,
                      backgroundColor: "rgba(255,255,255,0.25)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  />
                )}
                {!loading && (
                  <Typography
                    variant={isMobile ? "h4" : "h2"}
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      color: "white",
                      textShadow: "0 4px 20px rgba(0,0,0,0.5)",
                      mb: 1,
                    }}
                  >
                    {quote?.name && quote.name !== "-" ? `Hello, ${quote.name}` : "Welcome"}
                  </Typography>
                )}
                {!loading && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      fontWeight: 300,
                      maxWidth: "600px",
                    }}
                  >
                    {isSuccessStage
                      ? "Your booking has been confirmed"
                      : isPaymentStage
                      ? "Complete your booking with the deposit payment"
                      : isConfirmStage
                      ? "Please confirm your booking details"
                      : "Here is the personal quotation you requested"}
                  </Typography>
                )}
              </Box>
            </Grow>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ mt: -3, position: "relative", zIndex: 3 }}>
          {loading ? (
            <Fade in={true}>
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: paperRadius,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                }}
              >
                <LinearProgress
                  sx={{
                    maxWidth: 300,
                    mx: "auto",
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "rgba(56,0,69,0.1)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: mainColor,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ mt: 2, color: "#666" }}>
                  Loading your quote...
                </Typography>
              </Paper>
            </Fade>
          ) : quote?.status === "CANCELLED" || quote?.status === "EXPIRED" || quote?.isExpired ? (
            <Fade in={true}>
              <Paper
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: paperRadius,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                }}
              >
                {quote?.status === "EXPIRED" || quote?.isExpired ? (
                  <>
                    <Typography variant="h6" sx={{ color: mainColor, mb: 1 }}>
                      This quote has expired
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#666" }}>
                      Looks like this one has passed its sell-by date! Get in touch and we'll put together a fresh quote for you.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ color: mainColor, mb: 1 }}>
                      Hmm, this quote has vanished into thin air
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#666" }}>
                      This quote is no longer active, but don't worry, we haven't gone anywhere! Get in touch and we'll sort you out.
                    </Typography>
                  </>
                )}
              </Paper>
            </Fade>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 4,
              }}
            >
              {/* Vertical Timeline - Sticky on desktop */}
              {quote && !isAdminView && (
                <Box
                  sx={{
                    width: isMobile ? "100%" : 280,
                    flexShrink: 0,
                    position: isMobile ? "relative" : "sticky",
                    top: isMobile ? 0 : 24,
                    alignSelf: "flex-start",
                    zIndex: 10,
                  }}
                >
                  <VerticalTimeline
                    currentStage={currentStage}
                    completedStages={completedStages}
                    onStageClick={handleStageClick}
                    mainColor={mainColor}
                    accentColor={accentColor}
                    hasDeposit={hasDeposit && !isDepositPaid}
                    daysUntilExpiry={quote?.daysUntilExpiry}
                    isAccepted={quote?.isAccepted}
                    teamMember={quote?.teamMember}
                  />
                </Box>
              )}

              {/* Main Content Area */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Error Display */}
                {error && (
                  <Alert
                    severity="error"
                    sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(211, 47, 47, 0.15)" }}
                  >
                    {error}
                  </Alert>
                )}

                {/* SECTION 1: Quote Details */}
                {quote && confirmation?.bookingStatus !== "EXPIRED" && (
                  <Paper
                    ref={quoteSectionRef}
                    sx={{
                      borderRadius: paperRadius,
                      boxShadow: "0 20px 60px rgba(56,0,69,0.08)",
                      background: "white",
                      overflow: "hidden",
                    }}
                  >
                    {/* Collapsible Header */}
                    <Box
                      onClick={() => completedStages.includes(STAGES.QUOTE) && setQuoteSectionExpanded(!quoteSectionExpanded)}
                      sx={{
                        p: { xs: 3, md: 4 },
                        textAlign: "center",
                        cursor: completedStages.includes(STAGES.QUOTE) ? "pointer" : "default",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: completedStages.includes(STAGES.QUOTE)
                            ? "rgba(56,0,69,0.02)"
                            : "transparent",
                        },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: accentColor, fontWeight: 700, letterSpacing: 2 }}
                      >
                        YOUR EVENT PROPOSAL
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant={isMobile ? "h5" : "h4"}
                          sx={{ color: mainColor, fontWeight: 700 }}
                        >
                          Summary of Services
                        </Typography>
                        {completedStages.includes(STAGES.QUOTE) && (
                          <Box sx={{ display: "flex", alignItems: "center", color: mainColor, opacity: 0.7 }}>
                            {quoteSectionExpanded ? (
                              <ExpandLessIcon sx={{ fontSize: 28 }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: 28 }} />
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Collapsed summary */}
                      {!quoteSectionExpanded && (
                        <Box sx={{ mt: 2 }}>
                          <Chip
                            icon={<CheckCircleIcon sx={{ color: "#2e7d32 !important" }} />}
                            label="Quote Accepted"
                            sx={{
                              backgroundColor: "#e8f5e9",
                              color: "#2e7d32",
                              fontWeight: 600,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                            {quote.eventsList?.length || 0} event{(quote.eventsList?.length || 0) !== 1 ? "s" : ""} •
                            £{getTotalPrice().toFixed(2)} total
                          </Typography>
                        </Box>
                      )}

                      {quoteSectionExpanded && (
                        <Divider
                          sx={{
                            width: 60,
                            mx: "auto",
                            mt: 2,
                            borderColor: accentColor,
                            borderWidth: 2,
                          }}
                        />
                      )}
                    </Box>

                    {/* Collapsible Content */}
                    <Collapse in={quoteSectionExpanded} timeout={450}>
                      <Box sx={{ px: { xs: 3, md: 5 }, pb: { xs: 3, md: 5 } }}>
                        {/* External Notes - only show if present */}
                        {quote.externalNotes && (
                          <Box
                            sx={{
                              mb: 4,
                              p: 3,
                              backgroundColor: '#faf5ff',
                              borderRadius: 2,
                              borderLeft: `4px solid ${mainColor}`,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: mainColor,
                                mb: 1,
                              }}
                            >
                              {quote.teamMember?.name
                                ? `A note from ${getFirstName(quote.teamMember)}`
                                : "Event Notes"}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: '#475569',
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.6,
                              }}
                            >
                              {quote.externalNotes}
                            </Typography>
                          </Box>
                        )}

                        <QuoteAccordion
                          quote={quote}
                          quoteId={quoteId}
                          isAccepted={quote.isAccepted}
                          localSelections={localSelections}
                          onSelectionChange={handleSelectionChange}
                          expenses={quote.expensesList}
                          serverTotalPrice={quote.totalPrice}
                        />

                        {/* Accept Button (only on quote stage) */}
                        {!quote.isAccepted && !isAdminView && (
                          <Box sx={{ mt: 4, textAlign: "center" }}>
                            <QueryClientProvider client={queryClient}>
                              <AcceptRejectButton
                                quoteId={quoteId}
                                isAccepted={quote.isAccepted}
                                allOptionsSelected={checkAllOptionsSelected()}
                                localSelections={localSelections}
                                eventsWithOptions={eventsWithOptions}
                              />
                            </QueryClientProvider>
                          </Box>
                        )}

                        {/* Contact info */}
                        {!quote.isAccepted && !isAdminView && (
                          <Box sx={{ textAlign: "center", mt: 4 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Questions before you decide?
                            </Typography>
                            <Button
                              startIcon={<EmailIcon />}
                              variant="text"
                              href="mailto:info@imperialdrummers.com"
                              sx={{
                                color: "#666",
                                textTransform: "none",
                                "&:hover": {
                                  color: mainColor,
                                  backgroundColor: "transparent",
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              Contact us at info@imperialdrummers.com
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                )}

                {/* SECTION 2: Confirm Details */}
                {quote?.isAccepted && confirmation && confirmation?.bookingStatus !== "EXPIRED" && (
                  <Paper
                    ref={confirmSectionRef}
                    sx={{
                      borderRadius: paperRadius,
                      boxShadow: "0 20px 60px rgba(56,0,69,0.08)",
                      background: "white",
                      overflow: "hidden",
                    }}
                  >
                    {/* Collapsible Header */}
                    <Box
                      onClick={() => confirmation.isAlreadyConfirmed && setConfirmSectionExpanded(!confirmSectionExpanded)}
                      sx={{
                        p: { xs: 3, md: 4 },
                        textAlign: "center",
                        cursor: confirmation.isAlreadyConfirmed ? "pointer" : "default",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: confirmation.isAlreadyConfirmed
                            ? "rgba(56,0,69,0.02)"
                            : "transparent",
                        },
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: accentColor, fontWeight: 700, letterSpacing: 2 }}
                      >
                        BOOKING DETAILS
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant={isMobile ? "h5" : "h4"}
                          sx={{ color: mainColor, fontWeight: 700 }}
                        >
                          Confirm Your Information
                        </Typography>
                        {confirmation.isAlreadyConfirmed && (
                          <Box sx={{ display: "flex", alignItems: "center", color: mainColor, opacity: 0.7 }}>
                            {confirmSectionExpanded ? (
                              <ExpandLessIcon sx={{ fontSize: 28 }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: 28 }} />
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Collapsed summary */}
                      {confirmation.isAlreadyConfirmed && !confirmSectionExpanded && (
                        <Box sx={{ mt: 2 }}>
                          <Chip
                            icon={<CheckCircleIcon sx={{ color: "#2e7d32 !important" }} />}
                            label="Details Confirmed"
                            sx={{
                              backgroundColor: "#e8f5e9",
                              color: "#2e7d32",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      )}

                      {(confirmSectionExpanded || !confirmation.isAlreadyConfirmed) && (
                        <Divider
                          sx={{
                            width: 60,
                            mx: "auto",
                            mt: 2,
                            borderColor: accentColor,
                            borderWidth: 2,
                          }}
                        />
                      )}
                    </Box>

                    {/* Confirmed Details (Read-only) */}
                    <Collapse in={confirmation.isAlreadyConfirmed && confirmSectionExpanded} timeout={450}>
                      <Box sx={{ px: { xs: 3, md: 5 }, pb: { xs: 3, md: 5 } }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="First Name"
                              value={confirmation.customer?.firstname || "-"}
                              disabled
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "#666",
                                },
                                "& .MuiOutlinedInput-root.Mui-disabled": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Last Name"
                              value={confirmation.customer?.lastname || "-"}
                              disabled
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "#666",
                                },
                                "& .MuiOutlinedInput-root.Mui-disabled": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Email"
                              value={confirmation.customer?.email || "-"}
                              disabled
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "#666",
                                },
                                "& .MuiOutlinedInput-root.Mui-disabled": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              value={confirmation.customer?.phone || "-"}
                              disabled
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "#666",
                                },
                                "& .MuiOutlinedInput-root.Mui-disabled": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Address"
                              value={confirmation.customer?.address || "-"}
                              disabled
                              multiline
                              rows={2}
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "#666",
                                },
                                "& .MuiOutlinedInput-root.Mui-disabled": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>

                    {/* Form Content (Editable) */}
                    {!confirmation.isAlreadyConfirmed && (
                      <Box sx={{ px: { xs: 3, md: 5 }, pb: { xs: 3, md: 5 } }}>
                        {submitError && (
                          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            {submitError}
                          </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="First Name"
                                variant="outlined"
                                required
                                value={formData.firstname}
                                onChange={handleChange("firstname")}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Last Name"
                                variant="outlined"
                                required
                                value={formData.lastname}
                                onChange={handleChange("lastname")}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                variant="outlined"
                                required
                                value={formData.email}
                                onChange={handleChange("email")}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Phone Number"
                                variant="outlined"
                                required
                                value={formData.phone}
                                onChange={handleChange("phone")}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Address"
                                variant="outlined"
                                required
                                multiline
                                rows={2}
                                value={formData.address}
                                onChange={handleChange("address")}
                              />
                            </Grid>
                          </Grid>

                          {/* Terms & Conditions */}
                          <Box
                            sx={{
                              mt: 4,
                              p: 3,
                              backgroundColor: "rgba(56,0,69,0.02)",
                              borderRadius: 3,
                              border: "1px solid rgba(56,0,69,0.1)",
                            }}
                          >
                            <Typography variant="h6" sx={{ color: mainColor, mb: 2 }}>
                              Terms & Conditions
                            </Typography>
                            <Box
                              sx={{
                                maxHeight: 150,
                                overflowY: "auto",
                                mb: 2,
                                p: 2,
                                backgroundColor: "white",
                                borderRadius: 2,
                                fontSize: "0.875rem",
                                color: "#555",
                                lineHeight: 1.6,
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Delays &middot; Weather &middot; Changes &amp; Cancellation &middot; Payment &middot; Deposit
                              </Typography>
                              <Typography variant="body2">
                                Please review our full terms by clicking the link below. They cover
                                delays, weather conditions, changes and cancellation, payment and
                                deposit policy, along with any terms agreed specifically for your
                                booking.
                              </Typography>
                            </Box>

                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.termsAccepted}
                                  onChange={handleChange("termsAccepted")}
                                  sx={{
                                    color: mainColor,
                                    "&.Mui-checked": { color: mainColor },
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  I have read and agree to the{" "}
                                  <Link to={`/terms/${quoteId}`} style={{ color: mainColor }} onClick={(e) => e.stopPropagation()}>
                                    Terms and Conditions
                                  </Link>
                                </Typography>
                              }
                            />
                          </Box>

                          {/* Signature */}
                          <Box
                            sx={{
                              mt: 4,
                              p: 3,
                              backgroundColor: "rgba(212,175,55,0.05)",
                              borderRadius: 3,
                              border: "1px solid rgba(212,175,55,0.3)",
                            }}
                          >
                            <Typography variant="h6" sx={{ color: mainColor, mb: 1 }}>
                              Digital Signature
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                              Please type your full name below to confirm your booking
                            </Typography>
                            <TextField
                              fullWidth
                              label="Type your full name"
                              variant="outlined"
                              required
                              value={formData.signatureName}
                              onChange={handleChange("signatureName")}
                              placeholder="e.g. John Smith"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  fontStyle: "italic",
                                  fontSize: "1.1rem",
                                },
                              }}
                            />
                          </Box>

                          {/* Submit Button */}
                          <Box sx={{ mt: 4, textAlign: "center" }}>
                            <Button
                              type="submit"
                              variant="contained"
                              size="large"
                              disabled={submitting}
                              sx={{
                                backgroundColor: mainColor,
                                px: 6,
                                py: 1.5,
                                borderRadius: "50px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                textTransform: "none",
                                boxShadow: "0 8px 24px rgba(56,0,69,0.3)",
                                "&:hover": {
                                  backgroundColor: "#4a0058",
                                  boxShadow: "0 12px 32px rgba(56,0,69,0.4)",
                                },
                                "&:disabled": {
                                  backgroundColor: "#999",
                                },
                              }}
                            >
                              {submitting
                                ? "Confirming..."
                                : (hasDeposit && !isDepositPaid)
                                ? "Confirm & Continue to Payment"
                                : "Confirm Booking"}
                            </Button>
                          </Box>
                        </form>
                      </Box>
                    )}
                  </Paper>
                )}

                {/* SECTION 3: Payment or Success */}
                {confirmation?.isAlreadyConfirmed && (
                  <Paper
                    ref={finalSectionRef}
                    sx={{
                      borderRadius: paperRadius,
                      boxShadow: "0 10px 30px rgba(56,0,69,0.12)",
                      overflow: "hidden",
                    }}
                  >
                    {hasDeposit && !isDepositPaid ? (
                      <>
                        {/* Deposit Amount Header */}
                        <Box sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
                          <Typography
                            variant="overline"
                            sx={{ color: accentColor, fontWeight: 700, letterSpacing: 2 }}
                          >
                            SECURE YOUR BOOKING
                          </Typography>
                          <Typography
                            variant={isMobile ? "h5" : "h4"}
                            sx={{ color: mainColor, fontWeight: 700, mt: 1 }}
                          >
                            Deposit Required
                          </Typography>
                          <Divider
                            sx={{
                              width: 60,
                              mx: "auto",
                              mt: 2,
                              borderColor: accentColor,
                              borderWidth: 2,
                            }}
                          />
                          <Typography
                            variant="h2"
                            sx={{ color: mainColor, fontWeight: 700, mt: 3 }}
                          >
                            £{confirmation?.depositAmount?.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                            Remaining balance of £{(confirmation?.totalPrice - confirmation?.depositAmount)?.toFixed(2)} to be cleared 7 days before your event
                          </Typography>
                        </Box>

                        {renderBankDetails()}
                      </>
                    ) : (
                      /* Success - No Deposit Required */
                      <Box sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
                        {confirmation?.bookingStatus === "EXPIRED" ? (
                          <>
                            <Typography
                              variant="overline"
                              sx={{ color: accentColor, fontWeight: 700, letterSpacing: 2 }}
                            >
                              THAT'S A WRAP
                            </Typography>
                            <Typography
                              variant={isMobile ? "h4" : "h3"}
                              sx={{ color: mainColor, fontWeight: 700, mt: 2, mb: 3 }}
                            >
                              This one's in the past!
                            </Typography>
                            <Divider sx={{ width: 60, mx: "auto", mb: 4, borderColor: accentColor, borderWidth: 2 }} />
                            <Typography variant="body1" sx={{ color: "#666", maxWidth: 460, mx: "auto", lineHeight: 1.8 }}>
                              Looks like this event has already happened. We hope it was a great one! Get in touch if you'd like to book us again.
                            </Typography>
                          </>
                        ) : (
                          <>
                        <Typography
                          variant="overline"
                          sx={{ color: accentColor, fontWeight: 700, letterSpacing: 2 }}
                        >
                          YOU'RE ALL SET
                        </Typography>
                        <Typography
                          variant={isMobile ? "h4" : "h3"}
                          sx={{ color: mainColor, fontWeight: 700, mt: 2, mb: 3 }}
                        >
                          Booking Confirmed!
                        </Typography>
                        <Divider
                          sx={{
                            width: 60,
                            mx: "auto",
                            mb: 4,
                            borderColor: accentColor,
                            borderWidth: 2,
                          }}
                        />

                        {confirmation?.daysUntilEvent !== null && confirmation?.daysUntilEvent !== undefined && (
                          <Box sx={{ ...infoCardSx, mb: 4, textAlign: "center" }}>
                            <Typography
                              variant="h1"
                              sx={{
                                color: mainColor,
                                fontWeight: 800,
                                fontSize: { xs: "3.5rem", md: "4.5rem" },
                              }}
                            >
                              {confirmation.daysUntilEvent}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ color: "#666", fontWeight: 400 }}
                            >
                              {confirmation.daysUntilEvent === 1 ? "day" : "days"} until your event
                            </Typography>
                          </Box>
                        )}

                        <Typography
                          variant="body1"
                          sx={{
                            color: "#666",
                            maxWidth: 500,
                            mx: "auto",
                            lineHeight: 1.8,
                          }}
                        >
                          Thank you for choosing Imperial Drummers. We're excited to be part of your special day!
                          A confirmation email has been sent with all the details.
                        </Typography>

                        {hasRemainingBalance && (
                          <Box sx={{ ...infoCardSx, mt: 4, textAlign: "left" }}>
                            {/* Breakdown lines (only when a deposit has been paid) */}
                            {depositPaid && confirmation?.depositAmount != null && (
                              <>
                                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                                  <Typography variant="body2" sx={{ color: "#666" }}>
                                    Total
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#444" }}>
                                    {formatCurrency(confirmation.totalPrice)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                                  <Typography variant="body2" sx={{ color: "#666" }}>
                                    Deposit paid
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#666" }}>
                                    −{formatCurrency(confirmation.depositAmount)}
                                  </Typography>
                                </Box>
                                <Divider sx={{ my: 1.25, borderColor: "rgba(56,0,69,0.1)" }} />
                              </>
                            )}

                            {/* Balance due — the amount still owed */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <Typography variant="subtitle1" sx={{ color: mainColor, fontWeight: 600 }}>
                                Balance due
                              </Typography>
                              <Typography variant="h6" sx={{ color: mainColor, fontWeight: 700 }}>
                                {formatCurrency(remainingBalance)}
                              </Typography>
                            </Box>

                            <Typography
                              variant="caption"
                              sx={{ color: "#888", display: "block", mt: 1.5 }}
                            >
                              To be cleared 7 days before your event
                            </Typography>
                          </Box>
                        )}

                        {hasRemainingBalance && renderBankDetails()}

                        <Box sx={{ mt: 4 }}>
                          <Button
                            variant="text"
                            startIcon={<EmailIcon />}
                            href="mailto:info@imperialdrummers.com"
                            sx={{
                              color: mainColor,
                              textTransform: "none",
                              "&:hover": {
                                backgroundColor: "rgba(56,0,69,0.05)",
                              },
                            }}
                          >
                            Contact us if you have any questions
                          </Button>
                        </Box>
                          </>
                        )}
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            py: 1.5,
            textAlign: "center",
            backgroundColor: "rgba(250, 249, 246, 0.95)",
            borderTop: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <Link
            to={`/terms/${quoteId}`}
            style={{
              color: "#666",
              fontSize: "0.875rem",
              textDecoration: "none",
            }}
          >
            Terms and Conditions
          </Link>
        </Box>
      </Box>
    </Fade>
  );
};

export default QuotePage;
